import { Flex } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import DocumentAvailabilityIcon from '@/components/shared/DocumentAvailabilityIcon';
import TreeNode from '@/components/ui/TreeNode';
import { getIconColorByContentType } from '@/entities/Document';
import { TreeNodeFile, getDocumentAvailabilityIconReason } from '@/entities/Tree';
import { TreeStore } from '@/stores/local/shared/TreeStore';

import s from './TreeFile.module.scss';

type Props = {
  store: TreeStore;
  model: TreeNodeFile;
  actions?: React.ReactNode;
  indicator?: React.ReactNode;
  onClick: () => void;
  className?: string;
};

const TreeFile: React.FC<Props> = ({ store, model, actions, indicator, onClick, className }) => {
  const { documentId } = useParams();
  const { isOpen, meta, level } = store;
  const isLocked = !model.contentAvailable;
  const availabilityReason = getDocumentAvailabilityIconReason(model);
  const showAvailabilityIcon = availabilityReason !== null || isLocked;

  const title = (
    <Flex align="center" gap={6} className={s.root__title}>
      <span className={cn(s['root__title-text'], isLocked && s['root__title-text_muted'])}>{model.name}</span>
    </Flex>
  );

  const hasOriginalName = !!model.fileName && model.fileName !== model.name;

  const titleTooltip = (
    <Flex direction="column" gap={2}>
      <span>Название: {model.name}</span>
      {hasOriginalName && <span>Имя файла: {model.fileName}</span>}
    </Flex>
  );

  const icon = (
    <span className={s.root__icon}>
      <span className={cn(s.root__indicator, !indicator && s['root__indicator_placeholder'])}>{indicator}</span>
      <Flex align="center" gap={4} className={s['root__icon-wrap']}>
        {showAvailabilityIcon ? (
          <DocumentAvailabilityIcon reason={availabilityReason} />
        ) : (
          <IconFileText
            size={16}
            color={getIconColorByContentType(model.contentType)}
            className={s['root__file-icon']}
          />
        )}
      </Flex>
    </span>
  );

  return (
    <TreeNode
      className={className}
      isActive={documentId === model.id}
      title={title}
      tooltip={titleTooltip}
      withArrow={model.hasChildren}
      isOpen={isOpen}
      isLoading={meta.isLoading}
      onClick={onClick}
      level={level}
      actions={actions}
      icon={icon}
      isDocument
      isContentLocked={isLocked}
    />
  );
};

export default observer(TreeFile);
